/**
 * Default catalog of user-curiosity dimensions. Generic — designed to
 * cover most city-/region-walks. Track-specific dimensions live in
 * artrack metadata and override this list when a SpecialTrack is on.
 */
export const AVAILABLE_INTERESTS = [
    { slug: 'geschichte', label: 'Geschichte', emoji: '🕰' },
    { slug: 'kultur', label: 'Kultur & Architektur', emoji: '🏛' },
    { slug: 'flora_fauna', label: 'Flora & Fauna', emoji: '🌿' },
    { slug: 'geologie', label: 'Geologie & Natur', emoji: '⛰' },
    { slug: 'kulinarik', label: 'Kulinarik', emoji: '🍷' },
    { slug: 'kunst', label: 'Kunst', emoji: '🎨' },
    { slug: 'musik', label: 'Musik', emoji: '🎵' },
    { slug: 'outdoor', label: 'Outdoor & Wandern', emoji: '🥾' },
];
const STORAGE_KEY = 'gps-tracker-interests';
export class InterestsState {
    constructor(_bus) {
        this._bus = _bus;
        this._selected = new Set(this._resolveInitial());
    }
    /** Currently selected slugs (sorted by AVAILABLE_INTERESTS order). */
    get current() {
        return AVAILABLE_INTERESTS
            .filter((i) => this._selected.has(i.slug))
            .map((i) => i.slug);
    }
    /** All catalog options for view rendering. */
    get available() {
        return AVAILABLE_INTERESTS;
    }
    /** Is the given slug currently selected? */
    has(slug) {
        return this._selected.has(slug);
    }
    /** Toggle one slug on/off. Emits 'interests:changed' on any state shift. */
    toggle(slug) {
        if (!AVAILABLE_INTERESTS.some((i) => i.slug === slug)) {
            return; // unknown slug — silently ignore
        }
        if (this._selected.has(slug)) {
            this._selected.delete(slug);
        }
        else {
            this._selected.add(slug);
        }
        this._persist();
        this._bus.emit('interests:changed', { slugs: this.current });
    }
    /** Replace selection wholesale (used for URL bootstrap). */
    setAll(slugs) {
        const valid = slugs.filter((s) => AVAILABLE_INTERESTS.some((i) => i.slug === s));
        const next = new Set(valid);
        if (next.size === this._selected.size &&
            [...next].every((s) => this._selected.has(s))) {
            return; // no change
        }
        this._selected = next;
        this._persist();
        this._bus.emit('interests:changed', { slugs: this.current });
    }
    /** Clear all selections. */
    clear() {
        if (this._selected.size === 0)
            return;
        this._selected.clear();
        this._persist();
        this._bus.emit('interests:changed', { slugs: this.current });
    }
    /**
     * URL ?interests=a,b > localStorage > empty. URL param is sticky for
     * the session AND written to localStorage so a refresh keeps the
     * choice even if the param drops.
     */
    _resolveInitial() {
        try {
            const raw = new URLSearchParams(window.location.search).get('interests');
            if (raw && raw.trim()) {
                const slugs = raw
                    .split(',')
                    .map((s) => s.trim().toLowerCase())
                    .filter((s) => AVAILABLE_INTERESTS.some((i) => i.slug === s));
                if (slugs.length) {
                    window.localStorage.setItem(STORAGE_KEY, slugs.join(','));
                    return slugs;
                }
            }
            const stored = window.localStorage.getItem(STORAGE_KEY);
            if (stored && stored.trim()) {
                return stored.split(',').filter((s) => AVAILABLE_INTERESTS.some((i) => i.slug === s));
            }
        }
        catch {
            // localStorage may be blocked
        }
        return [];
    }
    _persist() {
        try {
            window.localStorage.setItem(STORAGE_KEY, this.current.join(','));
        }
        catch {
            // localStorage may be blocked
        }
    }
}
//# sourceMappingURL=InterestsState.js.map